import { useMsal } from "@azure/msal-react";
import * as React from "react";
import { AddCircle20Regular } from "@fluentui/react-icons";
import { msalConfig } from "../../accounts/types/msalAuthConfig";
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { ILookupDictionary } from "../../lookup/types/ILookupDictionary";
import { setAdjustmentDataSource } from "../../redux/reducers/RepertoireReducer";
import IRepertoireComponentDataItem from "../../redux/types/IRepertoireComponentDataItem";
import SelectorView from "../components/selectorView/SelectorView";
import { ALL_ROLES, EMPTY_STRING_VALUE, INTRAY, POSTED, SEARCH_VIEW_ADJUSTMENTS, UPDATE_ADJUSTMENTS_ROLE, VIEW_ADJUSTMENTS_ROLE, VIEW_DISTRIBUTIONS_ROLE, VIEW_POOLS_ROLE } from "../Consts";
import { IAdjustment } from "../types/usageTypes/IAdjustment";
import { IAdjustmentSearchQuery } from "../types/usageTypes/IAdjustmentSearchQuery";
import { IDistribution } from "../types/usageTypes/IDistribution";
import SearchResultsTable from "../components/searchResultsTable/SearchResultsTable";
import { IRepertoireField } from "../types/IRepertoireField";
import { IMembersPortalField } from "../../membersportal/types/IMembersPortalField";
import { SizedTextDataInput } from "../components/textDataInput/TextDataInput";
import { SizedDropdownDataInput } from "../components/dropdownDataInput/DropdownDataInput";
import SearchViewKeyEventListener from "../components/searchView/SearchViewKeyEventListener";
import { isMandatoryField } from "../../util/tools";
import { IRepertoireStateKeys } from "../types/IRepertoireStateKeys";
import SizedDatePickerDataInput from "../components/dateDataInput/DatePickerDataInput";
import { getLookupValues, mapLookupKey } from "../../lookup/services/LookupHelpers";
import ActionButton from "../components/actionButton/ActionButton";
import SearchableMultiSelectComboBox from "../components/usageComponents/searchView/searchableMultiSelect/SearchableMultiSelectComboBox";
import { buildUniqueCodeDropdownOptions } from "../components/usageComponents/searchView/UsageSearchHelper";

export interface IAdjustmentPageProps {
    adjustmentDataSource: string;
    setAdjustmentDataSource: typeof setAdjustmentDataSource;
    adjustmentsPageData: IRepertoireComponentDataItem;
    adjustmentGeneralDataView: IRepertoireComponentDataItem;
    searchViewData: IRepertoireComponentDataItem;
    searchResultsTableData: IRepertoireComponentDataItem;
    searchAdjustments: (query: IAdjustmentSearchQuery, dataSource: string) => void;
    addNewAdjustment: (lookupValues: ILookupDictionary, isNew: boolean, adjustmentGeneralDataView: IRepertoireComponentDataItem) => void;
    adjustmentsSearchResults: IAdjustment[];
    searchSuccessful: boolean;
    lookupValues: ILookupDictionary;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    currentPage?: number;
    totalResults?: number;
    resetPagination: (repertoireSection: string) => void;
    updatePagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    sortSearchResults: (name: string, results: any) => void;
    getDistributions?: () => void;
    getPools?: () => void;
    distributions?: IDistribution[];
    pools?: { code: string; description: string; }[];
    users?: { code: string; description: string; }[];
    getAdjustmentDetails: (adjustmentId: string, adjustments: IAdjustment[]) => void;
    roles?: string[];
    saveRoles?: (roles: string[]) => void;
}

const createInitialAdjustmentSearchState = (dataSource: string): IAdjustmentSearchQuery => ({
    id: "",
    adjustmentDateFrom: "",
    adjustmentDateTo: "",
    user: "",
    datasource: dataSource ?? "",
    sourceDistribution: "",
    sourcePool: "",
    adjustmentDistribution: "",
    adjustmentCategory: "",
    adjustmentStatus: "",
    adjustmentBatchID: "",
    workNumber: "",
    productNumber: "",
    ipNameNumber: "",
});

const FIELD_NAMES = {
    sourceDistribution: 'sourceDistribution',
    sourcePool: 'sourcePool',
    adjustmentDistribution: 'adjustmentDistribution',
    adjustmentCategory: 'adjustmentCategory',
    adjustmentStatus: 'adjustmentStatus',
    adjustmentBatchID: 'adjustmentBatchID',
    user: 'user',
    workNumber: 'workNumber',
    productNumber: 'productNumber',
    ipNameNumber: 'ipNameNumber',
}

const AdjustmentsPage = (props: IAdjustmentPageProps) => {
    const [loaded, setLoaded] = React.useState(false);
    const [adjustmentSearchState, setAdjustmentSearchState] = React.useState<IAdjustmentSearchQuery>(createInitialAdjustmentSearchState(props.adjustmentDataSource));
    const { instance, accounts } = useMsal();

    React.useEffect(() => {
        checkIfLoaded();
        getRoles();

        const account = accounts[0];
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, []);

    React.useEffect(() => {
        const { getDistributions, getPools, distributions, pools, roles } = props;
        checkIfLoaded();

        if (roles?.length > 0 && (distributions?.length ?? 0) === 0 && (roles.includes(VIEW_DISTRIBUTIONS_ROLE) || roles.includes(ALL_ROLES))) {
            getDistributions?.();
        }
        if (roles?.length > 0 && (pools?.length ?? 0) === 0 && (roles.includes(VIEW_POOLS_ROLE) || roles.includes(ALL_ROLES))) {
            getPools?.();
        }
    }, [props, loaded]);

    React.useEffect(() => {
        const { getDistributions, getPools, roles } = props;
        if (roles?.includes(VIEW_DISTRIBUTIONS_ROLE) || roles?.includes(ALL_ROLES)) {
            getDistributions?.();
        }
        if (roles?.includes(VIEW_POOLS_ROLE) || roles?.includes(ALL_ROLES)) {
            getPools?.();
        }
    }, [loaded]);

    React.useEffect(() => {
        setAdjustmentSearchState((prev) => ({
            ...prev,
            datasource: props.adjustmentDataSource,
        }));
    }, [props.adjustmentDataSource]);

    const checkIfLoaded = () => {
        const { adjustmentsPageData, roles } = props;
        if (adjustmentsPageData.fields && adjustmentsPageData.fields.length >= 1 && !loaded) {
            if ((roles?.length ?? 0) > 0) {
                setLoaded(true);
            }
            const header = adjustmentsPageData.fields.find(f => f.name === "PageTitle");
            if (header) {
                document.title = header.data;
            }
        }
    };

    const addNewAdjustment = (): void => {
        const { addNewAdjustment, adjustmentGeneralDataView, lookupValues } = props;
        addNewAdjustment(lookupValues, true, adjustmentGeneralDataView);
    };

    const getAdjustmentDetail = (adjustmentId: string) => {
        const { adjustmentsSearchResults, getAdjustmentDetails } = props;
        getAdjustmentDetails(adjustmentId, adjustmentsSearchResults);
    };

    const scope = {
        scopes: [msalConfig.auth.scope]
    };

    const getRoles = () => {
        const { saveRoles } = props;

        instance.acquireTokenSilent({
            ...scope,
            account: accounts[0]
        }).then((response) => {
            const decodedJwtToken = ComponentsHelper.parseJwt(response.accessToken);
            const roles = decodedJwtToken.roles;
            saveRoles?.(roles);
        }).catch(() => {
            saveRoles?.([ALL_ROLES]);
        });
    };

    const {
        adjustmentDataSource,
        setAdjustmentDataSource,
        searchViewData,
        searchResultsTableData,
        searchAdjustments,
        adjustmentsSearchResults,
        adjustmentsPageData,
        indexOfFirstResult,
        indexOfLastResult,
        resultsPerPage,
        currentPage,
        totalResults,
        resetPagination,
        updatePagination,
        sortSearchResults,
        distributions,
        pools,
        users,
    } = props;

    const { lookupValues } = props;

    const getUpdateRoleDisabled = (): boolean => {
        const { roles } = props;
        if (roles && (roles.includes(UPDATE_ADJUSTMENTS_ROLE) || roles.includes(ALL_ROLES))) {
            return false;
        }
        return true;
    };

    const getViewRoleDisabled = (): boolean => {
        const { roles } = props;
        if (roles && (roles.includes(VIEW_ADJUSTMENTS_ROLE) || roles.includes(ALL_ROLES))) {
            return false;
        }
        return true;
    };

    const setAdjustmentField = (fieldName: IRepertoireStateKeys, value: any) => {
        const normalizedValue = value === undefined || value === null ? "" : value;
        setAdjustmentSearchState((prev) => ({
            ...prev,
            [fieldName]: normalizedValue,
            ...(fieldName === "sourceDistribution" ? { sourcePool: "" } : {}),
        }));
    };

    const changeTextData = (value: any, fieldName: IRepertoireStateKeys) => {
        setAdjustmentField(fieldName, value);
    };

    const changeData = (value: any, fieldName: IRepertoireStateKeys) => {
        setAdjustmentField(fieldName, value);
    };

    const keyDownSubmit = (value: any) => {
        if (value.key === 'Enter' && !getViewRoleDisabled()) {
            onClickSearch();
        }
    };

    const onClickSearch = () => {
        const searchQuery: IAdjustmentSearchQuery = {
            ...adjustmentSearchState,
            datasource: adjustmentDataSource,
        };

        resetPagination(SEARCH_VIEW_ADJUSTMENTS);
        searchAdjustments(searchQuery, adjustmentDataSource);
    };

    const resetSearchState = () => setAdjustmentSearchState(createInitialAdjustmentSearchState(adjustmentDataSource));

    const renderSearchButton = () => {
        const searchButtonLabel = searchViewData?.fields?.find((field) => field.name === "search")?.data ?? "Search";

        return (
            <ActionButton
                buttonText={searchButtonLabel}
                buttonAction={onClickSearch}
                disabled={getViewRoleDisabled()}
                type="button"
            />
        );
    };

    const renderDropDownField = (searchField: IRepertoireField | IMembersPortalField, fieldValue: any, index: number, options: { code: string, description: string }[]) => {
        return (
                    <SizedDropdownDataInput
                    label={searchField.data}
                    fieldName={searchField.name}
                    changeData={changeData}
                    value={fieldValue ?? EMPTY_STRING_VALUE}
                    options={options}
                    allowNull={true}
                    isHidden={searchField.hidden}
                    componentInstance={SEARCH_VIEW_ADJUSTMENTS}
                    readOnly={false}
                    key={`${index}`}
                    isMandatory={isMandatoryField(searchField)}
                    />
        );
    };


    const renderSearchFields = () => {
        if (!searchViewData?.fields) {
            return null;
        }

        const filteredSearchFields = searchViewData.fields.filter(
            (searchField: IRepertoireField | IMembersPortalField) =>
                searchField.componentInstance === SEARCH_VIEW_ADJUSTMENTS &&
                searchField.name !== 'search'
        );

        const filteredSearchFieldsMapper = (searchField: IRepertoireField | IMembersPortalField, index: number): React.ReactElement => {
            const fieldValue = adjustmentSearchState[searchField.name as keyof IAdjustmentSearchQuery];

            if (searchField.fieldType === 'text' || searchField.fieldType === 'integer' || searchField.fieldType === 'decimal') {
                return (
                    <SizedTextDataInput
                        fieldType={searchField.fieldType}
                        useEnterSubmit={true}
                        label={searchField.data}
                        fieldName={searchField.name}
                        changeData={changeTextData}
                        handleKeyDown={keyDownSubmit}
                        value={fieldValue ?? EMPTY_STRING_VALUE}
                        readOnly={false}
                        isHidden={searchField.hidden}
                        key={`${index}`}
                        isMandatory={isMandatoryField(searchField)}
                    />
                );
            }

            if (searchField.fieldType === 'date') {
                return (
                    <SizedDatePickerDataInput
                        value={fieldValue ?? EMPTY_STRING_VALUE}
                        fieldName={searchField.name}
                        changeData={changeTextData}
                        label={searchField.data}
                        isHidden={searchField.hidden}
                        key={`${index}`}
                        isMandatory={isMandatoryField(searchField)}
                    />
                );
            }

            let options: { code: string, description: string }[] = [];

            if (searchField.name === FIELD_NAMES.sourceDistribution || searchField.name === FIELD_NAMES.adjustmentDistribution) {
                options = (distributions ?? []).map((x) => ({ code: x.distributionCode, description: x.description }));
                const formattedOptions = buildUniqueCodeDropdownOptions(options);

                return (
                    <div key={index} className="col-lg-4 col-md-4 col-sm-6 col-xs-12 form-group">
                        <div className="dropdownDataInput">
                            <SearchableMultiSelectComboBox
                                label={searchField.data}
                                options={formattedOptions}
                                selectedKeys={fieldValue ? [String(fieldValue)] : []}
                                onSelectionChange={(selectedKeys) =>
                                    changeData(selectedKeys[0] ?? EMPTY_STRING_VALUE, searchField.name as IRepertoireStateKeys)
                                }
                                searchPlaceholder="Search distributions"
                                noResultsText="No distributions found"
                                required={isMandatoryField(searchField)}
                                multiSelect={false}
                            />
                        </div>
                    </div>
                );
            }
            if (searchField.name === FIELD_NAMES.user) {
                options = users ?? [];

                return renderDropDownField(searchField, fieldValue, index, options);
            }
         
            if (searchField.name === FIELD_NAMES.sourcePool) {
                if (adjustmentSearchState.sourceDistribution) {
                    const sourceDist = (distributions ?? []).find(d => d.distributionCode === adjustmentSearchState.sourceDistribution);
                    const sourcePoolsList = sourceDist?.distributionPools ?? [];
                    options = [...new Set(sourcePoolsList.map(p => p.poolCode))].sort().map(poolCode => ({ code: poolCode, description: poolCode }));
                }
                else {
                    options = pools ?? [];
                }
                return renderDropDownField(searchField, fieldValue, index, options);
            }

            const isLookups = (lookupValues && getLookupValues(mapLookupKey(searchField.name, ''), lookupValues))
            if (isLookups) {
                options = getLookupValues(mapLookupKey(searchField.name, ''), lookupValues);
            }

            if ((searchField.name === FIELD_NAMES.adjustmentStatus)) {
                if (!(adjustmentDataSource === INTRAY)) {
                  return;
                } 
                return renderDropDownField(searchField, fieldValue, index, options);
            }

            if ((searchField.name === FIELD_NAMES.adjustmentCategory)) {
                return renderDropDownField(searchField, fieldValue, index, options);
            }
            return renderDropDownField(searchField, fieldValue, index, options);
        };

        return (
            <>
                <SearchViewKeyEventListener
                    setStateInitial={resetSearchState}
                    onSave={onClickSearch}
                    componentInstance={SEARCH_VIEW_ADJUSTMENTS}
                />
                {filteredSearchFields.map(filteredSearchFieldsMapper)}
            </>
        );
    };

    if (loaded) {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12">
                        <span className="title">Find Adjustment</span>
                        <div className="newWorkButton" title="Create New Adjustment" hidden={getUpdateRoleDisabled()}>
                            <div className="IconTextButton" onClick={addNewAdjustment}>
                                <AddCircle20Regular
                                    className="icon"
                                    aria-hidden="true"
                                />
                                New Adjustment
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <SelectorView
                        selection={adjustmentDataSource}
                        setSelection={setAdjustmentDataSource}
                        option1={INTRAY}
                        option1Label={INTRAY}
                        option2={POSTED}
                        option2Label={POSTED}
                        selectorTitle={adjustmentsPageData.fields[1].data}
                    />
                </div>
                <div className="row flexWrapRow paddingVeryLarge">
                    {renderSearchFields()}
                </div>
                <div className="row">
                    <div className="form-group col-lg-4 col-lg-push-8 col-md-4 col-md-push-8 col-sm-6 col-sm-push-6 col-xs-12 col-xs-push-0">
                        {renderSearchButton()}
                    </div>
                </div>
                
                <div className="row">
                    <div className="col-md-12 searchResultsDiv">
                        {adjustmentsSearchResults && <SearchResultsTable
                            key={'adjustmentsSearchResultsTable'}
                            searchResultsTableData={searchResultsTableData}
                            tableContents={adjustmentsSearchResults}
                            componentInstance={SEARCH_VIEW_ADJUSTMENTS}
                            indexOfFirstResult={indexOfFirstResult}
                            indexOfLastResult={indexOfLastResult}
                            resultsPerPage={resultsPerPage}
                            currentPage={currentPage}
                            totalResults={totalResults}
                            updatePagination={updatePagination}
                            repertoireSection={SEARCH_VIEW_ADJUSTMENTS}
                            getAdjustmentDetails={getAdjustmentDetail}
                            sortSearchResults={sortSearchResults}
                        />}
                    </div>
                </div>
            </div>
        );
    }

    return <div>Loading ...</div>;
};

export default AdjustmentsPage;
